import { useEffect, useRef, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { isPanelOwned } from '../store/types';
import { isPanMode } from '../chrome/tool-store';
import { screenToWorld } from './viewport';

interface RectS { x: number; y: number; w: number; h: number }

export function Marquee() {
  const dispatch = useCanvasStore(s => s.dispatch);
  const [rect, setRect] = useState<RectS | null>(null);
  // Use a ref for the live rect so the effect closure always reads the latest value
  // without needing rect in the deps array (avoids re-attaching listeners on every move).
  const rectRef = useRef<RectS | null>(null);
  // Whether this drag should extend the existing selection (shift held at start).
  const additiveRef = useRef(false);

  useEffect(() => {
    let down = false;
    let startX = 0;
    let startY = 0;

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-frame-id]')) return;
      if (e.button !== 0) return;
      // Marquee runs with the Select tool (plain drag), or with Shift in any
      // tool. Pan mode (Hand / Space) without Shift is left to the pan handler.
      if (isPanMode() && !e.shiftKey) return;
      additiveRef.current = e.shiftKey;
      down = true;
      startX = e.clientX;
      startY = e.clientY;
      const initial: RectS = { x: startX, y: startY, w: 0, h: 0 };
      rectRef.current = initial;
      setRect(initial);
    };

    const onMove = (e: MouseEvent) => {
      if (!down) return;
      const x = Math.min(startX, e.clientX);
      const y = Math.min(startY, e.clientY);
      const w = Math.abs(e.clientX - startX);
      const h = Math.abs(e.clientY - startY);
      const next: RectS = { x, y, w, h };
      rectRef.current = next;
      setRect(next);
    };

    const onUp = () => {
      if (!down) return;
      down = false;
      const current = rectRef.current;
      if (!current || (current.w === 0 && current.h === 0)) {
        rectRef.current = null;
        setRect(null);
        return;
      }
      const vp = useCanvasStore.getState().doc.viewport;
      const a = screenToWorld({ x: current.x, y: current.y }, vp);
      const b = screenToWorld({ x: current.x + current.w, y: current.y + current.h }, vp);
      const st = useCanvasStore.getState();
      const boxed = st.doc.frames
        .filter(f => !isPanelOwned(f))
        .filter(f => f.x + f.w >= a.x && f.x <= b.x && f.y + (f.h ?? 200) >= a.y && f.y <= b.y)
        .map(f => f.id);
      // Shift-drag extends the selection; a plain drag replaces it.
      const ids = additiveRef.current
        ? Array.from(new Set([...st.selection, ...boxed]))
        : boxed;
      dispatch({ t: 'select', ids });
      rectRef.current = null;
      setRect(null);
    };

    window.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dispatch]);

  if (!rect) return null;
  return (
    <div
      className="fixed border border-blue-500 bg-blue-500/10 pointer-events-none z-[60]"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
    />
  );
}
